// src/components/blog/BlogSection.tsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import BlogCard from "./BlogCard";
import { useTranslation } from "react-i18next";
import BlogDetailWrapper from "./BlogDetailWrapper";
import {
  GetPublicBlogs,
  Blog,
  PaginatedBlogsResponse,
} from "../../services/blog/GetPublicBlogs";
import "../../styles/blog/BlogSection.css";
import LoadingSpinner from "../utility/LoadingSpinner";
import WhatsAppBanner from "../app/WhatsAppBanner";
import Nav from "../app/Nav";
import SpiderWebBackground from "../app/SpiderWebBackground";

const BlogSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const location = useLocation();
  const pageSize = 10;
  const observer = useRef<IntersectionObserver | null>(null);
  const language = i18n.language;

  // Cargar blogs iniciales
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const data: PaginatedBlogsResponse = await GetPublicBlogs({
          page: 1,
          pageSize,
          language,
        });
        setBlogs(data.data);
        setHasMore(data.hasNext);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error al cargar blogs", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [language]);

  // Cargar más blogs cuando se llega al final
  const loadMoreBlogs = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const data: PaginatedBlogsResponse = await GetPublicBlogs({
        page: nextPage,
        pageSize,
        language,
      });

      setBlogs((prevBlogs) => [...prevBlogs, ...data.data]);
      setHasMore(data.hasNext);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error al cargar más blogs", error);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore, pageSize, language]);

  // Configurar Intersection Observer para detectar cuándo cargar más
  const lastBlogElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreBlogs();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore, loadMoreBlogs]
  );

  if (loading) {
    return (
      <>
        <section id="blogs" className="blog-section">
          <div className="blog-heading">
            <h1 className="blog-title">{t("blog.pageTitle")}</h1>
            <p className="blog-subtitle">{t("blog.pageSubtitle")}</p>
          </div>
          <LoadingSpinner />
        </section>
      </>
    );
  }

  return (
    <>
      <WhatsAppBanner />
      <Nav />
      <SpiderWebBackground />
      <Routes location={location}>
        <Route
          path="/"
          element={
            <section id="blogs" className="blog-section">
              <div className="blog-heading">
                <h1 className="blog-title">{t("blog.pageTitle")}</h1>
                <p className="blog-subtitle">{t("blog.pageSubtitle")}</p>
              </div>
              <div className="blog-grid">
                {blogs.length === 0 ? (
                  <p>{t("blog.noBlogs")}</p>
                ) : (
                  blogs.map((blog, index) => {
                    if (blogs.length === index + 1) {
                      return (
                        <div ref={lastBlogElementRef} key={blog.id}>
                          <BlogCard {...blog} />
                        </div>
                      );
                    } else {
                      return <BlogCard key={blog.id} {...blog} />;
                    }
                  })
                )}
              </div>
              {loadingMore && <LoadingSpinner />}
              {/*}
              {!hasMore && blogs.length > 0 && (
                <div className="no-more-blogs">
                  <p>{t("blog.noMoreBlogs")}</p>
                </div>
              )}
              {*/}
            </section>
          }
        />
        {/* Ruta para el detalle de un blog. Usará el 'slug' como parámetro */}
        <Route path=":slug" element={<BlogDetailWrapper blogs={blogs} />} />
      </Routes>
    </>
  );
};

export default BlogSection;
